import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Car as CarIcon } from "lucide-react";
import api, { fmtINR } from "../lib/api";
import { Page, StatusPill, Field } from "../components/Primitives";
import FileUploader from "../components/FileUploader";
import { toast } from "sonner";
import { motion } from "framer-motion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const emptyCar = {
  make: "",
  model: "",
  year: new Date().getFullYear(),
  variant: "",
  color: "",
  kms: 0,
  fuel: "Petrol",
  transmission: "",
  registration_no: "",
  vin: "",
  owners: 1,
  price: 0,
  cost_price: 0,
  rc_no: "",
  insurance_expiry: "",
  status: "available",
  notes: "",
  features: [],
  image_ids: [],
  doc_ids: [],
};

export default function Showroom() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCar);
  const [search, setSearch] = useState("");
  const nav = useNavigate();
  const status = params.get("status") || "";

  const load = async () => {
    const { data } = await api.get("/showroom/cars", {
      params: { status: status || undefined, q: search || undefined },
    });
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [status]);

  const startCreate = () => {
    setEditing(null);
    setForm(emptyCar);
    setOpen(true);
  };
  const startEdit = (c) => {
    setEditing(c.id);
    setForm({ ...emptyCar, ...c });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, year: +form.year, kms: +form.kms, price: +form.price, cost_price: +form.cost_price, owners: +form.owners };
      if (editing) await api.put(`/showroom/cars/${editing}`, payload);
      else await api.post("/showroom/cars", payload);
      toast.success(editing ? "Updated" : "Added to inventory");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const filters = [
    { key: "", label: "All" },
    { key: "available", label: "Available" },
    { key: "reserved", label: "Reserved" },
    { key: "sold", label: "Sold" },
  ];

  return (
    <Page
      number="02"
      title="Car Showroom"
      subtitle="Inventory of high-end vehicles on sale — documents, provenance and photography in one place."
      right={
        <button onClick={startCreate} className="kk-btn-primary" data-testid="add-car">
          <Plus size={14} /> Add Vehicle
        </button>
      }
    >
      <div className="flex flex-wrap gap-2 items-center mb-6">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setParams(f.key ? { status: f.key } : {})}
            className={"kk-pill " + (status === f.key ? "kk-pill-black" : "kk-pill-muted")}
            data-testid={`filter-${f.key || "all"}`}
          >
            {f.label}
          </button>
        ))}
        <div className="flex-1" />
        <input
          placeholder="Search make / model / reg"
          className="kk-input max-w-xs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          data-testid="car-search"
        />
      </div>

      {loading ? (
        <div className="text-sm text-[var(--kk-muted)]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-16 border border-dashed border-[var(--kk-border)] text-center">
          <CarIcon size={28} className="mx-auto text-[var(--kk-muted)]" />
          <div className="font-display text-xl uppercase mt-3">No vehicles yet</div>
          <p className="text-sm text-[var(--kk-muted)] mt-1">Add your first car or load demo data from the dashboard.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              whileHover={{ y: -4 }}
              className="kk-card p-0 group overflow-hidden cursor-pointer"
              onClick={() => nav(`/showroom/${c.id}`)}
              data-testid={`car-card-${c.id}`}
            >
              <div className="aspect-[16/10] bg-[var(--kk-surface-2)] relative overflow-hidden">
                {c.image_ids?.[0] ? (
                  <img src={`${BACKEND}/api/files/${c.image_ids[0]}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <CarIcon size={40} className="text-[var(--kk-border)]" />
                  </div>
                )}
                <div className="absolute top-3 right-3">
                  <StatusPill status={c.status} />
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="kk-overline">{c.year}</div>
                    <div className="font-display text-xl uppercase leading-tight">
                      {c.make} {c.model}
                    </div>
                    <div className="text-xs text-[var(--kk-muted)] mt-0.5">{c.variant || c.color}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-lg">{fmtINR(c.price)}</div>
                    <div className="text-[0.65rem] uppercase tracking-wider text-[var(--kk-muted)]">List</div>
                  </div>
                </div>
                <div className="flex gap-4 mt-4 text-xs font-mono text-[var(--kk-muted)]">
                  <span>{c.kms?.toLocaleString?.() || 0} km</span>
                  <span>·</span>
                  <span>{c.fuel}</span>
                  <span>·</span>
                  <span>{c.owners} owner{c.owners > 1 ? "s" : ""}</span>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-[var(--kk-border)] pt-3">
                  <div className="text-[0.7rem] font-mono text-[var(--kk-muted)]">{c.registration_no}</div>
                  <button
                    onClick={(e) => { e.stopPropagation(); startEdit(c); }}
                    className="text-xs underline uppercase tracking-wider"
                    data-testid={`edit-car-${c.id}`}
                  >
                    Edit
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Slide-over form */}
      {open && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setOpen(false)}>
          <motion.form
            initial={{ x: 400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            className="w-full max-w-2xl bg-[var(--kk-surface)] overflow-y-auto p-6 md:p-8"
          >
            <div className="kk-overline">{editing ? "Edit" : "New"}</div>
            <h2 className="font-display text-3xl uppercase">{editing ? "Update Vehicle" : "Add Vehicle"}</h2>
            <div className="grid grid-cols-2 gap-5 mt-6">
              <Field label="Make *"><input className="kk-input" required value={form.make} onChange={upd("make")} /></Field>
              <Field label="Model *"><input className="kk-input" required value={form.model} onChange={upd("model")} /></Field>
              <Field label="Year"><input className="kk-input" type="number" value={form.year} onChange={upd("year")} /></Field>
              <Field label="Variant"><input className="kk-input" value={form.variant || ""} onChange={upd("variant")} /></Field>
              <Field label="Color"><input className="kk-input" value={form.color || ""} onChange={upd("color")} /></Field>
              <Field label="Fuel"><input className="kk-input" value={form.fuel || ""} onChange={upd("fuel")} /></Field>
              <Field label="Transmission"><input className="kk-input" value={form.transmission || ""} onChange={upd("transmission")} /></Field>
              <Field label="KMs"><input className="kk-input" type="number" value={form.kms} onChange={upd("kms")} /></Field>
              <Field label="Owners"><input className="kk-input" type="number" value={form.owners} onChange={upd("owners")} /></Field>
              <Field label="Registration #"><input className="kk-input" value={form.registration_no || ""} onChange={upd("registration_no")} /></Field>
              <Field label="VIN / Chassis"><input className="kk-input" value={form.vin || ""} onChange={upd("vin")} /></Field>
              <Field label="RC Number"><input className="kk-input" value={form.rc_no || ""} onChange={upd("rc_no")} /></Field>
              <Field label="Insurance expiry"><input className="kk-input" type="date" value={form.insurance_expiry || ""} onChange={upd("insurance_expiry")} /></Field>
              <Field label="Cost Price (₹)"><input className="kk-input" type="number" value={form.cost_price} onChange={upd("cost_price")} /></Field>
              <Field label="List Price (₹)"><input className="kk-input" type="number" value={form.price} onChange={upd("price")} /></Field>
              <Field label="Status">
                <select className="kk-input" value={form.status} onChange={upd("status")}>
                  <option value="available">Available</option>
                  <option value="reserved">Reserved</option>
                  <option value="sold">Sold</option>
                </select>
              </Field>
              <div className="col-span-2">
                <Field label="Notes">
                  <textarea className="kk-input min-h-[80px]" value={form.notes || ""} onChange={upd("notes")} />
                </Field>
              </div>
              <div className="col-span-2">
                <FileUploader
                  category="showroom"
                  ids={form.image_ids}
                  onChange={(ids) => setForm({ ...form, image_ids: ids })}
                  label="Photos"
                />
              </div>
              <div className="col-span-2">
                <FileUploader
                  category="docs"
                  ids={form.doc_ids}
                  onChange={(ids) => setForm({ ...form, doc_ids: ids })}
                  label="Documents (RC / Insurance / Invoice)"
                  accept="application/pdf,image/*"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button type="submit" className="kk-btn-primary" data-testid="save-car">
                {editing ? "Update" : "Add to inventory"}
              </button>
              <button type="button" className="kk-btn-ghost" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </motion.form>
        </div>
      )}
    </Page>
  );
}
